import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { PublicRestaurantService } from './public-restaurant.service';

interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose?: string;
}

interface StaffRouteSpec {
  shortName: string;
  longName: string;
}

// Mapeo segmento -> copy del manifest. El segmento es lo que va DESPUES del slug
// en la URL: /<slug>/pos, /<slug>/mesero, /<slug>/cocina, /<slug>/barra, etc.
// Si Angular agrega nuevas rutas staff (ej. /<slug>/host), basta con sumarlas aca.
const STAFF_ROUTE_SPECS: Record<string, StaffRouteSpec> = {
  pos: { shortName: 'POS', longName: 'POS' },
  'pos-simple': { shortName: 'POS', longName: 'POS Simple' },
  mesero: { shortName: 'Mesero', longName: 'Mesero' },
  cocina: { shortName: 'Cocina', longName: 'Cocina' },
  barra: { shortName: 'Barra', longName: 'Barra' },
  kiosco: { shortName: 'Kiosko', longName: 'Kiosko' }
};

const DEFAULT_ICON_SIZES = [72, 96, 128, 144, 152, 192, 384, 512];
const DEFAULT_THEME_COLOR = '#F59E0B';

interface RestaurantBrandingCache {
  name: string;
  themeColor: string;
}

@Injectable({ providedIn: 'root' })
export class DynamicManifestService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly restaurantService = inject(PublicRestaurantService);

  private originalHref: string | null = null;
  private currentObjectUrl: string | null = null;
  private lastAppliedKey: string | null = null;
  private readonly brandingCache = new Map<string, RestaurantBrandingCache>();
  private readonly pendingFetches = new Set<string>();

  init(): void {
    if (!this.isBrowser()) return;
    this.captureOriginalHref();
    this.applyForUrl(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.applyForUrl(e.urlAfterRedirects));
  }

  private applyForUrl(url: string): void {
    const match = this.matchStaffRoute(url);
    if (!match) {
      this.restore();
      return;
    }

    const { slug, spec, startUrl } = match;
    const branding = this.brandingCache.get(slug);

    this.writeManifest({
      slug,
      spec,
      startUrl,
      restaurantName: branding?.name ?? null,
      themeColor: branding?.themeColor ?? DEFAULT_THEME_COLOR
    });

    if (!branding && !this.pendingFetches.has(slug)) {
      this.fetchAndCacheBranding(slug, () => {
        // Solo re-aplicar si la URL activa sigue siendo del mismo slug/segmento.
        const stillActive = this.matchStaffRoute(this.router.url);
        if (stillActive && stillActive.slug === slug) {
          this.applyForUrl(this.router.url);
        }
      });
    }
  }

  private writeManifest(opts: {
    slug: string;
    spec: StaffRouteSpec;
    startUrl: string;
    restaurantName: string | null;
    themeColor: string;
  }): void {
    const { spec, startUrl, restaurantName, themeColor } = opts;
    const fullName = restaurantName
      ? `${spec.longName} - ${restaurantName}`
      : spec.longName;

    // Evita re-escribir si nada cambio (URL identica + branding ya pintado).
    const key = `${startUrl}::${fullName}::${themeColor}`;
    if (key === this.lastAppliedKey) return;

    const link = this.findManifestLink();
    if (!link) return;

    // El manifest se sirve como blob: (URL.createObjectURL). Las URLs relativas NO
    // resuelven contra un blob: (no tiene base), asi que el browser ignora start_url,
    // scope e iconos ("URL is invalid") y la PWA no instala bien. Hay que absolutizarlas
    // con el origin de la pagina.
    const origin = this.doc.location?.origin ?? '';
    const manifest = {
      name: fullName,
      short_name: spec.shortName,
      description: fullName,
      start_url: `${origin}${startUrl}`,
      scope: `${origin}/`,
      display: 'standalone',
      background_color: '#ffffff',
      theme_color: themeColor,
      orientation: 'any',
      icons: this.defaultIcons(origin)
    };

    this.revokeCurrentObjectUrl();
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    this.currentObjectUrl = URL.createObjectURL(blob);
    link.setAttribute('href', this.currentObjectUrl);
    this.lastAppliedKey = key;
  }

  private restore(): void {
    if (this.lastAppliedKey === null) return;
    const link = this.findManifestLink();
    if (!link) return;
    this.revokeCurrentObjectUrl();
    if (this.originalHref !== null) link.setAttribute('href', this.originalHref);
    this.lastAppliedKey = null;
  }

  // Devuelve { slug, spec, startUrl } si la URL es una pantalla staff conocida,
  // o null para el resto del sitio. startUrl preserva la URL exacta (incluye
  // sub-rutas como /pos/vender o /mesero/orders) para que el shortcut abra
  // donde el usuario lo creo, no en el login.
  private matchStaffRoute(url: string): { slug: string; spec: StaffRouteSpec; startUrl: string } | null {
    const cleanPath = this.stripQueryAndHash(url);
    const segments = cleanPath.split('/').filter(s => s.length > 0);
    if (segments.length < 2) return null;

    const [slug, screen] = segments;
    const spec = STAFF_ROUTE_SPECS[screen];
    if (!spec) return null;
    if (!this.isValidSlug(slug)) return null;

    return { slug, spec, startUrl: cleanPath };
  }

  private fetchAndCacheBranding(slug: string, onDone: () => void): void {
    this.pendingFetches.add(slug);
    this.restaurantService
      .getRestaurantInfo(slug)
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          this.pendingFetches.delete(slug);
          if (!resp?.succeeded || !resp.data) return;
          this.brandingCache.set(slug, {
            name: resp.data.name,
            themeColor: resp.data.primaryColor || DEFAULT_THEME_COLOR
          });
          onDone();
        },
        error: () => {
          this.pendingFetches.delete(slug);
        }
      });
  }

  private captureOriginalHref(): void {
    if (this.originalHref !== null) return;
    const link = this.findManifestLink();
    if (link) this.originalHref = link.getAttribute('href');
  }

  private findManifestLink(): HTMLLinkElement | null {
    return this.doc.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  }

  private revokeCurrentObjectUrl(): void {
    if (!this.currentObjectUrl) return;
    URL.revokeObjectURL(this.currentObjectUrl);
    this.currentObjectUrl = null;
  }

  private defaultIcons(origin: string): ManifestIcon[] {
    return DEFAULT_ICON_SIZES.map(size => ({
      src: `${origin}/icons/icon-${size}x${size}.png`,
      sizes: `${size}x${size}`,
      type: 'image/png',
      purpose: 'any'
    }));
  }

  private stripQueryAndHash(url: string): string {
    const queryIdx = url.indexOf('?');
    const hashIdx = url.indexOf('#');
    const cuts = [queryIdx, hashIdx].filter(i => i >= 0);
    return cuts.length ? url.substring(0, Math.min(...cuts)) : url;
  }

  // Filtra falsos positivos: rutas globales (superadmin, owner, info, etc.)
  // no son slugs de restaurante. Cualquier path conocido del root va aca para
  // que no se intente leer "/superadmin/pos" como si fuera un slug.
  private isValidSlug(candidate: string): boolean {
    if (!candidate) return false;
    if (RESERVED_ROOT_PATHS.has(candidate)) return false;
    // Slugs validos: minusculas, numeros, guiones. Defensa contra rutas raras.
    return /^[a-z0-9][a-z0-9-]*$/.test(candidate);
  }

  private isBrowser(): boolean {
    return typeof this.doc !== 'undefined' && !!this.doc?.querySelector;
  }
}

// Mantener sincronizado con app.routes.ts — cualquier path declarado en la raiz
// que NO sea `:slug` debe estar aca para que no se confunda con un restaurante.
const RESERVED_ROOT_PATHS = new Set<string>([
  'info',
  'catalog',
  'catalogo',
  'posibles-clientes',
  'posibles-domiciliarios',
  'centro-ayuda',
  'contacto',
  'aviso-legal',
  'privacidad',
  'superadmin',
  'restaurant',
  'owner',
  'unauthorized',
  'inventory',
  'encuesta'
]);
